import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import RadixSelect from '../../../shared/RadixSelect';
import { ME, previewAsMatches } from '../utils/recipientPreview';

// Sized like the size-1 Radix Select this replaced: a compact trigger as
// wide as the selected name. The width is capped so typing a long search
// can't push the header's audience chips onto a second line.
// RadixSelect merges these over its own Radix styles, so keys it already
// styles (control, valueContainer, menu, option) return only what changes;
// the rest start from react-select's `provided`.
const compactStyles = {
  container: provided => ({ ...provided, minWidth: 140, maxWidth: 260 }),
  control: (_provided, state) => ({
    minHeight: 'var(--space-5)',
    height: 'var(--space-5)',
    borderRadius: 'var(--radius-2)',
    borderColor: state.isFocused ? 'var(--accent-8)' : 'var(--gray-a7)',
    boxShadow: 'none',
    backgroundColor: 'var(--color-surface)',
    fontSize: 'var(--font-size-1)',
    cursor: 'pointer',
    flexWrap: 'nowrap',
  }),
  valueContainer: () => ({
    padding: '0 0 0 var(--space-2)',
    flexWrap: 'nowrap',
  }),
  singleValue: provided => ({
    ...provided,
    position: 'static',
    transform: 'none',
    margin: 0,
    maxWidth: '100%',
    color: 'var(--gray-12)',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: provided => ({
    ...provided,
    padding: '0 var(--space-2) 0 var(--space-1)',
    color: 'var(--gray-12)',
    '& svg': { width: 14, height: 14 },
  }),
  menu: () => ({ minWidth: 240, marginTop: 4, zIndex: 10 }),
  // A quiet hint, not a heading: "50 of 40,000 · type to search".
  groupHeading: provided => ({
    ...provided,
    textTransform: 'none',
    fontSize: 11,
    fontWeight: 400,
    color: 'var(--gray-9)',
    whiteSpace: 'nowrap',
    marginBottom: 2,
  }),
  // Highlight like the Radix Select items did.
  option: (_provided, state) => ({
    fontSize: 'var(--font-size-1)',
    fontWeight: 400,
    padding: '6px var(--space-3)',
    backgroundColor: state.isFocused ? 'var(--accent-9)' : 'transparent',
    color: state.isFocused ? 'var(--accent-contrast)' : 'var(--gray-12)',
  }),
};

// Searchable "Preview as" picker for Review. It lists "Me" plus at most
// PREVIEW_AS_LIMIT recipients for what has been typed, so a large audience
// never renders thousands of options.
export const PreviewAsSelect = ({
  recipientRows,
  value,
  selectedName,
  meName,
  onChange,
}) => {
  const { t } = useTranslation('broadcasts');
  const [query, setQuery] = useState('');

  const meOption = useMemo(
    () => ({ value: ME, label: t('reviewStep.previewAsMe', { name: meName }) }),
    [t, meName],
  );

  const options = useMemo(() => {
    const { rows, total } = previewAsMatches(recipientRows, query);
    const people = rows.map(row => ({ value: row.userUuid, label: row.name }));
    // "Me" only while nothing is typed, so Enter picks the first match.
    const head = query.trim() ? [] : [meOption];

    if (total <= people.length) return [...head, ...people];

    return [
      ...head,
      {
        label: t('reviewStep.previewAsFirstOf', {
          shown: people.length,
          total: total.toLocaleString(),
        }),
        options: people,
      },
    ];
  }, [recipientRows, query, meOption, t]);

  const selected = useMemo(
    () => (!value || value === ME ? meOption : { value, label: selectedName }),
    [value, selectedName, meOption],
  );

  return (
    <RadixSelect
      aria-label={t('reviewStep.previewAs')}
      classNamePrefix="react-select"
      options={options}
      value={selected}
      onChange={option => onChange(option.value)}
      inputValue={query}
      onInputChange={setQuery}
      filterOption={null}
      isSearchable
      noOptionsMessage={() => t('reviewStep.previewAsNoMatches')}
      // Fixed rather than portaled: the menu escapes the header's overflow
      // but stays inside the Radix Theme, so its sizes and font still apply.
      menuPosition="fixed"
      styles={compactStyles}
    />
  );
};
